import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  TextField,
  Typography,
} from "@mui/material";
import { notesCard, notesLabel } from "../../style";
import { Form, Formik } from "formik";
import { errorToastMessage, toastMessage } from "../../../../../utils/toast";
import { InputWrapper } from "../../../../Common/styles/form";
import PatientHistory from "./PatientHistory";
import { useParams } from "react-router-dom";
import http from "../../../../../utils/http";
import { useState } from "react";
import ExtractGoalsDrawer from "./ExtractGoalsDrawer";
import { goalsApi } from "../../../../../utils/goalsApi";
import { useAppDispatch } from "../../../../../Redux/hooks";
import { setMyPatientsTab } from "../../../../../Redux/reducers/myPatientsSlice";

const initialValues = { notes: "" };

const PatientNotes = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const [refresh, setRefresh] = useState(false);
  const [loader, setLoader] = useState(false);
  const [extractedGoals, setExtractedGoals] = useState<string[]>([]);
  const [extractingGoals, setExtractingGoals] = useState(false);
  const [extractingNoteId, setExtractingNoteId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const handleSubmit = async (
    values: { notes: string },
    resetForm: any
  ) => {
    try {
      setLoader(true);
      if (!values.notes) {
        throw new Error("Notes is required");
      }
      const url = `/notes`;
      const body = {
        note: values.notes,
        participantId: id,
      };

      const res = await http.post(url, body);
      toastMessage("success", res.data?.message);
      setRefresh((prev) => !prev);
      resetForm();
      setLoader(false);
    } catch (error) {
      errorToastMessage(error as Error);
      setLoader(false);
    }
  };

  const handleExtractGoals = async (noteId: string) => {
    try {
      setExtractingGoals(true);
      setExtractingNoteId(noteId);
      setShowModal(true);
      const response = await goalsApi.extractGoals(noteId);
      setExtractedGoals(response.goals || []);
    } catch (error) {
      errorToastMessage(error as Error);
      setExtractingGoals(false);
      setShowModal(false);
    }
  };

  const handleLoadingComplete = () => {
    // All AI messages have been shown, now show the results
    setExtractingGoals(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setExtractedGoals([]);
    setExtractingGoals(false);
    setExtractingNoteId(null);
  };

  const handleGoalsCreated = () => {
    // Close modal and switch to Goals tab
    handleCloseModal();
    dispatch(setMyPatientsTab("goals"));
  };

  return (
    <>
      <Typography variant="h6" fontWeight="medium" sx={{ p: "5px" }}>
        Notes
      </Typography>
      <Box sx={notesCard}>
        <Formik
          initialValues={initialValues}
          onSubmit={(values, helpers) => {
            const { resetForm } = helpers;
            handleSubmit(values, resetForm);
          }}
        >
          {({ errors, touched, getFieldProps, resetForm }) => (
            <Form style={{ flexDirection: "column", gap: 15 }}>
              <FormControl sx={InputWrapper}>
                <FormLabel sx={notesLabel} htmlFor="notes">
                  Notes
                </FormLabel>
                <TextField
                  id="notes"
                  placeholder="Type your clinical notes here..."
                  multiline
                  minRows={6}
                  error={touched?.notes && errors?.notes ? true : false}
                  helperText={
                    touched?.notes && errors?.notes ? errors.notes : " "
                  }
                  sx={{
                    "& .MuiInputBase-root": {
                      bgcolor: "#FAFAFA",
                      "&.Mui-focused": {
                        bgcolor: "#FFFFFF",
                      },
                    },
                  }}
                  {...getFieldProps("notes")}
                />
              </FormControl>
              <Box
                sx={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}
              >
                {!loader ? (
                  <>
                    <Button variant="contained" type="submit">
                      Save Note
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Box sx={{ alignContent: "center" }}>
                    <CircularProgress />
                  </Box>
                )}
              </Box>
            </Form>
          )}
        </Formik>
        <PatientHistory
          refresh={refresh}
          onExtractGoals={handleExtractGoals}
          extractingNoteId={extractingNoteId}
          isExtracting={extractingGoals}
        />
      </Box>

      <ExtractGoalsDrawer
        open={showModal}
        onClose={handleCloseModal}
        goals={extractedGoals}
        loading={extractingGoals}
        participantId={id || ""}
        noteId={extractingNoteId || undefined}
        onLoadingComplete={handleLoadingComplete}
        onGoalsCreated={handleGoalsCreated}
      />
    </>
  );
};

export default PatientNotes;
